import { db } from '../db/schema';

export type QueueEntity =
  | 'advance'
  | 'message'
  | 'change-order'
  | 'document';

export type QueueStatus = 'pending' | 'syncing' | 'failed' | 'synced';

export interface QueueItem<T = any> {
  id: string;
  entity: QueueEntity;
  action: 'create' | 'update' | 'delete';
  payload: T;
  localRef: string;
  status: QueueStatus;
  attempts: number;
  lastError?: string;
  createdAt: number;
  updatedAt: number;
  syncedAt?: number;
}

export interface QueueTableLike<T> {
  add: (item: T) => Promise<string>;
  get: (id: string) => Promise<T | undefined>;
  update: (id: string, changes: Partial<T>) => Promise<number>;
  delete: (id: string) => Promise<void>;
  toArray: () => Promise<T[]>;
  where: (key: string) => {
    equals: (value: any) => {
      toArray: () => Promise<T[]>;
      count: () => Promise<number>;
      first: () => Promise<T | undefined>;
    };
  };
  count: () => Promise<number>;
  clear: () => Promise<void>;
  filter: (fn: (item: T) => boolean) => { toArray: () => Promise<T[]> };
}

export interface OfflineQueueDb {
  offlineQueue: QueueTableLike<QueueItem>;
}

const MAX_ATTEMPTS = 5;

function getQueueTable(): OfflineQueueDb['offlineQueue'] {
  return (db as unknown as OfflineQueueDb).offlineQueue;
}

export const offlineQueue = {
  async enqueue<T>(
    entity: QueueEntity,
    action: QueueItem['action'],
    payload: T,
    localRef: string
  ): Promise<string> {
    const now = Date.now();
    const item: QueueItem<T> = {
      id: crypto.randomUUID(),
      entity,
      action,
      payload,
      localRef,
      status: 'pending',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    };
    return getQueueTable().add(item as QueueItem);
  },

  async get(id: string): Promise<QueueItem | undefined> {
    return getQueueTable().get(id);
  },

  async listPending(): Promise<QueueItem[]> {
    const all = await getQueueTable().toArray();
    return all
      .filter((i) => i.status === 'pending' || i.status === 'failed')
      .sort((a, b) => a.createdAt - b.createdAt);
  },

  async listByEntity(entity: QueueEntity): Promise<QueueItem[]> {
    return getQueueTable()
      .where('entity')
      .equals(entity)
      .toArray();
  },

  async listByStatus(status: QueueStatus): Promise<QueueItem[]> {
    return getQueueTable().where('status').equals(status).toArray();
  },

  async markSyncing(id: string): Promise<void> {
    await getQueueTable().update(id, { status: 'syncing', updatedAt: Date.now() });
  },

  async markSynced(id: string): Promise<void> {
    await getQueueTable().update(id, {
      status: 'synced',
      syncedAt: Date.now(),
      updatedAt: Date.now(),
      lastError: undefined,
    });
  },

  async markFailed(id: string, error: string): Promise<void> {
    const item = await getQueueTable().get(id);
    if (!item) return;
    const attempts = item.attempts + 1;
    await getQueueTable().update(id, {
      attempts,
      status: attempts >= MAX_ATTEMPTS ? 'failed' : 'pending',
      lastError: error,
      updatedAt: Date.now(),
    });
  },

  async remove(id: string): Promise<void> {
    await getQueueTable().delete(id);
  },

  async countPending(): Promise<number> {
    const all = await getQueueTable().toArray();
    return all.filter((i) => i.status === 'pending' || i.status === 'failed' || i.status === 'syncing').length;
  },

  async countFailed(): Promise<number> {
    return getQueueTable().where('status').equals('failed').count();
  },

  async clearSynced(): Promise<void> {
    const synced = await getQueueTable().where('status').equals('synced').toArray();
    for (const item of synced) {
      await getQueueTable().delete(item.id);
    }
  },

  async updateRetry(id: string): Promise<void> {
    const item = await getQueueTable().get(id);
    if (!item) return;
    await getQueueTable().update(id, {
      status: 'pending',
      attempts: 0,
      lastError: undefined,
      updatedAt: Date.now(),
    });
  },
};